/* eslint react-hooks/exhaustive-deps: "warn" */
import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './LegalSanctions.module.scss';

type RowStatus = 'clear' | 'review' | 'hit';

type Row = {
  name: string;
  type: 'company' | 'person';
  status: RowStatus;
  note?: string;
  tag?: string;
};

const ROWS: Row[] = [
  { name: 'Halden Marine Services Ltd', type: 'company', status: 'clear', tag: 'Primary' },
  { name: 'Halden Group Holdings BV', type: 'company', status: 'clear' },
  {
    name: 'Brant Maritime Holdings SA',
    type: 'company',
    status: 'hit',
    note: 'OFAC SDN · ultimate parent, two steps above the subject',
  },
  { name: 'E. Halden', type: 'person', status: 'clear' },
  { name: 'M. Brant', type: 'person', status: 'review', note: 'Domestic PEP · office held since 2022' },
  { name: 'Kestrel Offshore GmbH', type: 'company', status: 'clear' },
  { name: 'Brant Chartering Pte Ltd', type: 'company', status: 'clear' },
  {
    name: 'Adlerhaven Bunkering AS',
    type: 'company',
    status: 'review',
    note: 'Name similarity 0.91 · adjudication required',
  },
  { name: 'A. Lindqvist', type: 'person', status: 'clear' },
  { name: 'Halden Bunkering Services Ltd', type: 'company', status: 'clear' },
  { name: 'R. Okonjo', type: 'person', status: 'clear' },
  { name: 'Brant Marine Insurance Ltd', type: 'company', status: 'clear' },
];

const TALLY = {
  clear: ROWS.filter((r) => r.status === 'clear').length,
  review: ROWS.filter((r) => r.status === 'review').length,
  hit: ROWS.filter((r) => r.status === 'hit').length,
};

const P1 = 900;
const P2 = 2150;
const ROW_AT = 2200;
const ROW_STEP = 200;
const SCAN_END = ROW_AT + ROWS.length * ROW_STEP;
const TOTAL = SCAN_END + 400;
const HOLD = 5200;
const LOOP = TOTAL + HOLD;

const STAGE = ['Scope', 'Group selected', 'Screening', `${TALLY.hit} hit`];
const LABEL = [
  'Ready to screen',
  'Resolving the group',
  `Screening ${ROWS.length} entities and individuals`,
  'Screening complete',
];

const ICON_BUILDING = <path d="M2.5 14V4.2L8 2l5.5 2.2V14M6 14v-3h4v3M5.5 6.5h1M9.5 6.5h1M5.5 8.8h1M9.5 8.8h1" />;
const ICON_PERSON = <path d="M8 8a2.6 2.6 0 1 0 0-5.2A2.6 2.6 0 0 0 8 8zM3 13.6c0-2.6 2.2-4.2 5-4.2s5 1.6 5 4.2" />;

export const LegalSanctions = () => {
  const { t } = useInt();
  const cardRef = useRef<HTMLDivElement>(null);
  const inView = useIntersection(cardRef, { threshold: 0.2, continuous: true });

  const [phase, setPhase] = useState(3);
  const [checkedCount, setCheckedCount] = useState(ROWS.length);
  const [scanningIndex, setScanningIndex] = useState(-1);
  const [progress, setProgress] = useState(100);
  const [groupSelected, setGroupSelected] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  const rafRef = useRef<number | null>(null);
  const tPrevRef = useRef(0);
  const elapsedRef = useRef(LOOP);
  const holdingRef = useRef(true);
  const lastRowRef = useRef(ROWS.length - 1);
  const phaseRef = useRef(3);

  const setPhaseValue = useCallback((n: number) => {
    if (phaseRef.current === n) {
      return;
    }
    phaseRef.current = n;
    setPhase(n);
    setGroupSelected(n >= 1);
    if (n === 3) {
      setScanningIndex(-1);
    }
  }, []);

  const markRow = useCallback((k: number) => {
    setScanningIndex(elapsedRef.current < SCAN_END ? k : -1);
    setCheckedCount(k + 1);
    setProgress(Math.round(((k + 1) / ROWS.length) * 100));
  }, []);

  const reset = useCallback(() => {
    elapsedRef.current = 0;
    phaseRef.current = -1;
    lastRowRef.current = -1;
    holdingRef.current = false;
    setCheckedCount(0);
    setScanningIndex(-1);
    setProgress(0);
    setPhaseValue(0);
  }, [setPhaseValue]);

  const frame = useCallback(
    (now: number) => {
      elapsedRef.current += now - tPrevRef.current;
      tPrevRef.current = now;

      if (holdingRef.current) {
        if (elapsedRef.current >= LOOP) {
          reset();
        }
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      const elapsed = elapsedRef.current;
      setPhaseValue(elapsed < P1 ? 0 : elapsed < P2 ? 1 : elapsed < SCAN_END + 200 ? 2 : 3);

      const want = Math.floor((elapsed - ROW_AT) / ROW_STEP);
      if (want > lastRowRef.current) {
        for (let k = Math.max(lastRowRef.current + 1, 0); k <= Math.min(want, ROWS.length - 1); k += 1) {
          markRow(k);
        }
        lastRowRef.current = Math.min(want, ROWS.length - 1);
      }

      if (elapsed >= TOTAL) {
        holdingRef.current = true;
      }

      rafRef.current = requestAnimationFrame(frame);
    },
    [reset, markRow, setPhaseValue],
  );

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    stop();
    tPrevRef.current = performance.now();
    rafRef.current = requestAnimationFrame(frame);
  }, [stop, frame]);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setReducedMotion(true);
    }
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      return undefined;
    }

    if (inView) {
      reset();
      start();
    } else {
      stop();
    }

    return () => stop();
  }, [inView, reducedMotion, reset, start, stop]);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.left} ref={cardRef}>
          <div className={cn(styles.card, phase === 3 && styles.cardDone)}>
            <div className={styles.head}>
              <span className={styles.headIcon}>
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                  <path d="M10 2.5l6 2.2v5c0 4-2.6 6.6-6 7.8-3.4-1.2-6-3.8-6-7.8v-5z" />
                </svg>
              </span>
              <span className={styles.headTtl}>
                PEPs and sanctions screening
                <small>Halden Marine Services Ltd</small>
              </span>
              <span className={cn(styles.stage, phase === 3 && styles.stageHit)}>{STAGE[phase]}</span>
            </div>

            <div className={styles.scope}>
              <div className={styles.scopeOpt}>
                <b>This entity</b>
                <span>Halden Marine Services Ltd</span>
                <span className={styles.scopeMut}>The party on the engagement letter</span>
                <span className={cn(styles.scopeMiss, phase === 3 && styles.scopeMissOn)}>
                  Would have returned nothing
                </span>
              </div>
              <div className={cn(styles.scopeOpt, styles.scopeOptGroup)}>
                <span className={cn(styles.tick, groupSelected && styles.tickOn)}>
                  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                    <path d="M2.5 7.4l3 3 6-6.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <b>Entity + group</b>
                <span>{ROWS.length} entities and individuals</span>
                <span className={styles.scopeMut}>Parents, subsidiaries, directors, shareholders</span>
              </div>
            </div>

            <div className={styles.prog}>
              <div className={styles.progLabel}>
                <span>{LABEL[phase]}</span>
                <span>{progress}%</span>
              </div>
              <div className={styles.progTrack}>
                <i style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className={cn(styles.tally, phase === 3 && styles.tallyOn)}>
              <span className={cn(styles.tl, styles.tlOk)}>{TALLY.clear} Clear</span>
              <span className={cn(styles.tl, styles.tlRev)}>{TALLY.review} Review</span>
              <span className={cn(styles.tl, styles.tlHit)}>{TALLY.hit} Hit</span>
            </div>

            <div className={styles.list}>
              {ROWS.map((row, i) => {
                const checked = i < checkedCount;
                const scanning = i === scanningIndex;
                const flagged = phase === 3 && row.status !== 'clear';
                return (
                  <div
                    className={cn(
                      styles.row,
                      checked && styles.rowChecked,
                      scanning && styles.rowScanning,
                      flagged && styles[`rowFlagged${row.status === 'hit' ? 'Hit' : 'Review'}`],
                    )}
                    key={row.name}
                  >
                    <span className={styles.rowIcon}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
                        {row.type === 'company' ? ICON_BUILDING : ICON_PERSON}
                      </svg>
                    </span>
                    <span className={styles.rowNm}>
                      {row.name}
                      {row.tag && <em>{row.tag}</em>}
                      {flagged && row.note && <small>{row.note}</small>}
                    </span>
                    <span className={styles.rowSlot}>
                      <i className={cn(styles.pend, checked && styles.pendOff)} />
                      <span
                        className={cn(
                          styles.st,
                          checked && styles.stOn,
                          styles[`st${row.status[0].toUpperCase()}${row.status.slice(1)}`],
                        )}
                      >
                        {row.status === 'clear' ? 'Clear' : row.status === 'review' ? 'Review' : 'Hit'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>

            <div className={styles.foot}>
              <span className={styles.footNote}>{ROWS.length} entities and individuals screened</span>
              <span className={cn(styles.cta, phase === 3 && styles.ctaOn)}>
                Review {TALLY.review + TALLY.hit} flagged
              </span>
            </div>
          </div>
        </div>

        <div className={styles.right}>
          <TitleBlock
            align="left"
            title={t('Sanctions and PEPs attach to the group and the people in it. Not to the name you screened.')}
            description={t(
              'A designation can sit on a parent two steps above your client, or on a director who never appears on the engagement letter. Screening the named party tests the one entity least likely to be flagged.',
            )}
          />

          <p className={styles.claim}>{t('Screening and group resolution have to be the same operation.')}</p>

          <div className={styles.blk}>
            <div className={styles.blkK}>{t('Coverage')}</div>
            <dl className={styles.spec}>
              <dt>{t('Sanctions')}</dt>
              <dd>{t('OFSI, OFAC, EU and UN consolidated lists, plus national and regional regimes')}</dd>
              <dt>{t('PEPs')}</dt>
              <dd>
                {t(
                  'Domestic, foreign and international-organisation, with relatives, close associates and former officeholders',
                )}
              </dd>
            </dl>
          </div>

          <div className={styles.blk}>
            <div className={styles.blkK}>{t('How it runs')}</div>
            <dl className={styles.spec}>
              <dt>{t('What gets screened')}</dt>
              <dd>{t('Every entity and every person in the resolved group')}</dd>
              <dt>{t('What each hit shows')}</dt>
              <dd>{t('The ownership path back to your client, with a match score')}</dd>
              <dt>{t('How often')}</dt>
              <dd>{t('Re-screened as designations change, not on a schedule')}</dd>
            </dl>
          </div>
        </div>
      </div>
    </Section>
  );
};
